import { ECONOMY_DEFAULTS, KINDS, KIND_DIRS, type ContentKinds, type Economy, type Kind, type ZoneName, type ZoneWeights } from './types';
import type { Building } from './types';

export interface Entry<K extends Kind = Kind> {
  kind: K;
  path: string; // relative path used for error messages, e.g. content/buildings/x.json
  data: ContentKinds[K];
}

/**
 * Single source of truth for all content, in tools and in the game.
 * Fill it with `addRaw` (any loader) then call `freeze()`.
 */
export class Registry {
  private maps: { [K in Kind]: Map<string, ContentKinds[K]> } = {
    terrain: new Map(), road: new Map(), building: new Map(), weapon: new Map(),
    vehicle: new Map(), biome: new Map(), zone: new Map(), place: new Map(), prop: new Map(), namelist: new Map(), item: new Map(), defence: new Map(), job: new Map(), gang: new Map(), economy: new Map(), ad: new Map(), cosmetic: new Map(), store: new Map(), animal: new Map(), chatter: new Map(), brand: new Map(), radio: new Map(), song: new Map(), station: new Map(), character: new Map(), media: new Map(), walker: new Map(), mission: new Map(), playlist: new Map(), help: new Map(), achievement: new Map(),
  };
  readonly entries: Entry[] = [];
  private zoneCache = new Map<string, unknown[]>();

  static kindFromPath(path: string): Kind | undefined {
    const norm = path.replace(/\\/g, '/');
    for (const k of KINDS) if (norm.includes(`/${KIND_DIRS[k]}/`) || norm.startsWith(`${KIND_DIRS[k]}/`)) return k;
    return undefined;
  }

  /** Add a parsed JSON document. Does NOT validate — that is the tool's job. */
  // (see also `add` for runtime-created entries such as the showroom place)
  addRaw(path: string, data: unknown): Entry | undefined {
    const kind = Registry.kindFromPath(path);
    if (!kind) return undefined;
    return this.add(kind, path, data as ContentKinds[Kind]);
  }

  add<K extends Kind>(kind: K, path: string, data: ContentKinds[K]): Entry<K> {
    const id = (data as { id?: string }).id ?? '';
    (this.maps[kind] as Map<string, ContentKinds[K]>).set(id, data);
    const e: Entry<K> = { kind, path, data };
    this.entries.push(e);
    this.zoneCache.clear();
    return e;
  }

  /** (owner, Sep 22) the players' own buildings (world/custom.ts): `custom:<lot>` ids the overlay's designs make; not content, not in `all()` */
  private customs = new Map<string, Building>();
  setCustom(def: Building): void { this.customs.set(def.id, def); }
  dropCustom(id: string): void { this.customs.delete(id); }
  customIds(): string[] { return [...this.customs.keys()]; }
  get<K extends Kind>(kind: K, id: string): ContentKinds[K] | undefined {
    if (kind === 'building') { const c = this.customs.get(id); if (c) return c as ContentKinds[K]; }
    return (this.maps[kind] as Map<string, ContentKinds[K]>).get(id);
  }
  need<K extends Kind>(kind: K, id: string): ContentKinds[K] {
    const v = this.get(kind, id);
    if (!v) throw new Error(`Missing ${kind} '${id}'`);
    return v;
  }
  all<K extends Kind>(kind: K): ContentKinds[K][] {
    return [...(this.maps[kind] as Map<string, ContentKinds[K]>).values()];
  }
  /** The economy tunables (content/economy/default.json), defaults filled in field by field. */
  get economy(): Economy { if (!this.econ) { const e = this.all('economy')[0]; this.econ = e ? { ...ECONOMY_DEFAULTS, ...e, robbery: { ...ECONOMY_DEFAULTS.robbery, ...e.robbery }, business: { ...ECONOMY_DEFAULTS.business, ...e.business }, ticket: { ...ECONOMY_DEFAULTS.ticket, ...e.ticket }, law: { ...ECONOMY_DEFAULTS.law, ...e.law }, tuning: { ...ECONOMY_DEFAULTS.tuning, ...e.tuning }, loot: { ...ECONOMY_DEFAULTS.loot, ...e.loot }, fuel: { ...ECONOMY_DEFAULTS.fuel, ...e.fuel }, turf: { ...ECONOMY_DEFAULTS.turf, ...e.turf }, karma: { ...ECONOMY_DEFAULTS.karma, ...e.karma } } : ECONOMY_DEFAULTS; } return this.econ; }
  private econ?: Economy;
  has(kind: Kind, id: string): boolean {
    return this.maps[kind].has(id) || (kind === 'building' && this.customs.has(id));
  }
  /**
   * (C) The sprites the owner picked on the admin panel's Graphics page (server/graphics.ts, `GET /sprites/index.json`):
   * "<kind>:<id>" → a sprite that replaces the entry's own, in place, before anything has drawn it. Unknown keys and
   * kinds without a sprite of their own are ignored, so an index from a newer server never breaks an older client.
   */
  overrideSprites(picks: Record<string, { size: [number, number]; shapes: unknown[] }>): number {
    const field: Partial<Record<Kind, string>> = { building: 'roof', vehicle: 'sprite', weapon: 'icon', prop: 'sprite', item: 'sprite', defence: 'sprite' };
    let n = 0;
    for (const [key, sprite] of Object.entries(picks)) {
      const i = key.indexOf(':'); if (i < 0) continue;
      const kind = key.slice(0, i) as Kind, id = key.slice(i + 1), f = field[kind];
      const e = f ? (this.maps[kind] as unknown as Map<string, Record<string, unknown>> | undefined)?.get(id) : undefined;
      if (!e || !sprite || !Array.isArray(sprite.size) || !Array.isArray(sprite.shapes) || !sprite.shapes.length) continue;
      e[f!] = sprite; n++;
    }
    return n;
  }
  count(kind: Kind): number {
    return this.maps[kind].size;
  }

  /** Entries of a kind that have a positive weight for a zone (cached). */
  forZone<K extends 'building' | 'vehicle' | 'road' | 'prop' | 'animal'>(kind: K, zone: ZoneName): ContentKinds[K][] {
    const key = `${kind}:${zone}`;
    let v = this.zoneCache.get(key) as ContentKinds[K][] | undefined;
    if (!v) {
      v = this.all(kind).filter((e) => ((e as { zones: ZoneWeights }).zones[zone] ?? 0) > 0);
      this.zoneCache.set(key, v);
    }
    return v;
  }

  zonesSorted(): ContentKinds['zone'][] {
    return this.all('zone').sort((a, b) => a.densityMin - b.densityMin);
  }
}
